import type { FunctionComponent, ReactNode } from 'react';
import { useCallback, useContext } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { asMoney, forTemplate } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import type { BillingIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { getOrganizationBillingUrl } from '@trello/urls';

import { useHasReverseTrialExperience } from 'app/src/components/FreeTrial';
import { PlanComparisonContext } from '../PlanComparisonContext';
import type { BillingQuote, ChangeStepFn } from '../types';
import { PlanCardTotals } from './PlanCardTotals';

import styles from './StandardPlanCard.less';

const format = forTemplate('billing_page_one');

export interface StandardPlanCardProps {
  numOfMembers?: number;
  teamDisplayName: ReactNode;
  teamName: string;
  prices?: BillingQuote;
  changeStep: ChangeStepFn;
  isMonthly: boolean;
  onShowFreeTrialModal: (planName: string) => void;
  sendAnalytics: (name: string) => void;
  isInPPCohort?: boolean;
}

export const StandardPlanCard: FunctionComponent<StandardPlanCardProps> = ({
  changeStep,
  isMonthly,
  prices,
  numOfMembers = 1,
  teamName,
  teamDisplayName,
  sendAnalytics,
  isInPPCohort,
}) => {
  const pricePerUser = isMonthly
    ? prices?.monthly?.subTotalPerUser
    : prices?.annual?.subTotalPerUser;

  const productCode = isMonthly
    ? prices?.monthly?.productCode
    : prices?.annual?.productCode;

  const {
    onClickGetStandard,
    isModal,
    hasEndOfTrialFrictionExperience,
    onChooseStandard,
  } = useContext(PlanComparisonContext);

  const onChooseStandardWithAnalytics = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'chooseStandardButton',
      source: 'trelloPlanPricingModal',
      attributes: {
        viaEndOfTrial: true,
      },
    });
    onChooseStandard?.();
  }, [onChooseStandard]);

  const conClickButtonInModal = hasEndOfTrialFrictionExperience
    ? onChooseStandardWithAnalytics
    : onClickGetStandard;

  const onClickUpgradeButton = () => {
    changeStep({
      selectedProductCode: productCode || null,
      step: 'billing',
      analyticsCb: () => sendAnalytics('standard'),
    });
  };

  const isReverseTrial = useHasReverseTrialExperience();

  return (
    <section className={styles.planCard}>
      <article>
        <h3 className={styles.name}>{format('standard-name')}</h3>
        <p className={styles.info}>
          {isInPPCohort
            ? format('standard-description-personal-productivity')
            : format('standard-description-repackaging-gtm')}
        </p>
        <div className={styles.priceContainer}>
          <span className={styles.price}>
            {format('amount-usd', {
              amount: asMoney(pricePerUser || 0).replace(/\.00$/, ''),
            })}
          </span>
          <span className={styles.frequency}>
            {format('per-user')}
            <br />
            {format(isMonthly ? 'per-month' : 'per-year')}
          </span>
        </div>
        <div className={styles.ctaContainer}>
          {isReverseTrial ? (
            <Button href={getOrganizationBillingUrl(teamName)}>
              Upgrade now
            </Button>
          ) : (
            <Button
              onClick={isModal ? conClickButtonInModal : onClickUpgradeButton}
              testId={
                isModal
                  ? getTestId<BillingIds>('get-standard')
                  : getTestId<BillingIds>('upgrade-team-button')
              }
            >
              {isModal ? format('get-standard') : format('upgrade-now')}
            </Button>
          )}
        </div>
      </article>
      <PlanCardTotals
        isMonthly={isMonthly}
        numOfMembers={numOfMembers}
        teamName={teamDisplayName}
        prices={prices}
      />
    </section>
  );
};
